import styles from "./WorkbookGrid.module.css";

import * as React from "react";
import PropTypes from "prop-types";

import { readExcelRows, saveGridLikeWorkbook } from "@maxis/client-logic/utils/componentUtil";
import { validate, getValidateObject, getObjectFromYml } from "@maxis/client-logic/utils/validationUtil";

class WorkbookGrid extends React.Component {
  useRefInput = React.createRef();
  useRefEditDiv = React.createRef();
  refGrid = React.createRef();
  headerRangeIdx = 0;

  constructor(props) {
    super(props);

    this.state = {
      source: this.normalizeRows(props.source || props.data || []),
      data: props.data,
      columns: props.columns,
      columnGroups: props.columnGroups,
      isReadOnly: props.isReadOnly,
      selectedRowIndex: -1,
      selectedColumnIndex: -1,
      validateInfo: null,
      cellEditStyle: [],
    };
  }

  componentDidMount() {
    this.syncGridRef();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.source !== this.props.source || prevProps.data !== this.props.data) {
      this.setState({ source: this.normalizeRows(this.props.source || this.props.data || []) });
      return;
    }

    if (prevProps.columns !== this.props.columns || prevProps.columnGroups !== this.props.columnGroups) {
      this.setState({
        columns: this.props.columns,
        columnGroups: this.props.columnGroups,
      });
      return;
    }

    if (
      prevState.source !== this.state.source ||
      prevState.columns !== this.state.columns ||
      prevState.columnGroups !== this.state.columnGroups ||
      prevState.selectedRowIndex !== this.state.selectedRowIndex
    ) {
      this.syncGridRef();
    }
  }

  normalizeRows = (datasource) => {
    if (!datasource) return [];
    if (typeof datasource === "string") return JSON.parse(datasource);
    return Array.isArray(datasource) ? datasource : [];
  };

  getDisplayColumns = () => {
    const columns = this.chkArray(this.state.columnGroups)
      ? this.setFlatColumnGroups(this.state.columnGroups)
      : this.state.columns;
    return (columns || []).filter((column) => column && column.visible !== false);
  };

  getExportColumns = () => {
    return this.getDisplayColumns().filter((column) => column.binding !== "ROW_CHECK" && column.binding !== "ROW_STATUS");
  };

  syncGridRef = () => {
    const rows = this.getRows() || [];
    const columns = this.getDisplayColumns();
    const selectedRowIndex = this.state.selectedRowIndex;
    const adapter = {
      hostElement: this.containerRef,
      rows: rows.map((row, index) => ({ dataItem: row, index, dataIndex: index, isReadOnly: this.state.isReadOnly })),
      columns,
      _cols: columns,
      itemsSource: rows,
      selectedRows: selectedRowIndex > -1 ? [{ index: selectedRowIndex, dataItem: rows[selectedRowIndex] }] : [],
      selection: {
        row: selectedRowIndex,
        col: this.state.selectedColumnIndex,
      },
      collectionView: {
        items: rows,
        _view: rows,
        currentItem: selectedRowIndex > -1 ? rows[selectedRowIndex] : null,
        currentPosition: selectedRowIndex,
        itemCount: rows.length,
        moveCurrentToPosition: (idx) => this.selectionByIndex(idx),
        removeAt: (idx) => this.removeRowAt(idx),
        remove: (row) => this.removeRowAt(rows.indexOf(row)),
      },
      sheets: [{ name: "defaultSheet", itemsSource: { items: rows } }],
      selectedSheet: { name: "defaultSheet", itemsSource: { items: rows } },
      selectedSheetIndex: 0,
      select: (row, col = 0) => this.selectionByIndex(row, col),
      refresh: () => this.forceUpdates(),
    };

    this.refGrid.current = adapter;
    if (this.props.isSearchFilter?.current?.control) {
      this.props.isSearchFilter.current.control.grid = adapter;
    }
  };

  render() {
    const columns = this.getDisplayColumns();
    const rows = this.getRows() || [];
    const tableStyle = {
      width: typeof this.props.width === "number" ? `${this.props.width}px` : this.props.width || "100%",
      height: typeof this.props.height === "number" ? `${this.props.height}px` : this.props.height || "100%",
      ...this.props.style,
    };

    return (
      <div
        className={styles.container}
        ref={(node) => {
          this.containerRef = node;
        }}
      >
        {this.props.isInputFilter ? this.renderFilters(columns) : null}
        <div className={styles.sheetWrap} style={tableStyle}>
          <table className={styles.sheetTable}>
            <thead>
              <tr>
                {columns.map((column, colIndex) => (
                  <th key={`${column.binding || column.header}_${colIndex}`} style={this.getColumnStyle(column)}>
                    {column.header || column.binding}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={row.__MAXIS_ROW_ID || rowIndex}
                  className={rowIndex === this.state.selectedRowIndex ? styles.selectedRow : ""}
                  onClick={() => this.selectionByIndex(rowIndex)}
                  onDoubleClick={() => this.props.onRowDoubleClick?.()}
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={`${column.binding || column.header}_${rowIndex}_${colIndex}`}
                      className={this.getCellClass(row, column)}
                      onClick={() => this.selectionByIndex(rowIndex, colIndex)}
                    >
                      {this.renderCell(row, column, rowIndex)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {this.props.exportExcel ? (
          <button type="button" onClick={() => this.saveExcel()} className={styles.exportButton}>
            Export
          </button>
        ) : null}
        <input
          ref={this.useRefInput}
          className="input-excel-import"
          style={{ display: "none" }}
          type="file"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={(e) => this.loadExcelMakeGridData(e.target.files[0])}
        />
        <div ref={this.useRefEditDiv} style={{ display: "none" }} />
      </div>
    );
  }

  renderFilters(columns) {
    return (
      <div className={styles.filterRow}>
        {columns.map((column, index) => (
          <input
            key={`${column.binding || column.header}_${index}`}
            className={styles.filterInput}
            style={this.getColumnStyle(column)}
            onKeyUp={(e) => this.applyColumnFilter(column.binding, e.target.value)}
          />
        ))}
      </div>
    );
  }

  renderCell(row, column, rowIndex) {
    const binding = column.binding;
    const value = row?.[binding] ?? "";
    const readOnly = this.state.isReadOnly || column.isReadOnly;

    if (column.isButton) {
      return (
        <button type="button" className={styles.cellButton} onClick={(e) => column.clickEvent?.(e, { item: row })}>
          {value || column.header || "Button"}
        </button>
      );
    }

    if (column.isImage) {
      return (
        <img
          className={styles.cellImage}
          src={value || column.src || ""}
          alt={column.header || binding || ""}
          onClick={(e) => column.clickEvent?.(e, { item: row })}
        />
      );
    }

    if (column.isLink) {
      return (
        <a href={value || "#"} onClick={(e) => column.clickEvent?.(e, { item: row })}>
          {value || column.header || "Link"}
        </a>
      );
    }

    if (binding === this.props.checkBoxColumn || column.dataType === "Boolean") {
      return (
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={readOnly}
          onChange={(e) => this.setCellData(rowIndex, binding, e.target.checked)}
        />
      );
    }

    if (readOnly) return this.formatCellValue(value, column);

    return (
      <input
        className={styles.cellInput}
        value={value ?? ""}
        onChange={(e) => this.setCellData(rowIndex, binding, e.target.value)}
      />
    );
  }

  getColumnStyle(column) {
    if (!column?.width) return {};
    return { width: typeof column.width === "number" ? `${column.width}px` : column.width };
  }

  getCellClass(row, column) {
    if (column.binding !== "ROW_STATUS") return "";
    if (row.ROW_STATUS === 1) return styles.rowInsert;
    if (row.ROW_STATUS === 2) return styles.rowUpdate;
    if (row.ROW_STATUS === 4) return styles.rowDelete;
    return "";
  }

  formatCellValue(value, column) {
    if (value == null) return "";
    if (value instanceof Date) return value.toLocaleDateString();
    if (column?.dataMap && Array.isArray(column.dataMap)) {
      const match = column.dataMap.find((item) => item[column.dataMapCode] === value);
      if (match) return match[column.dataMapValue];
    }
    return String(value);
  }

  applyColumnFilter(binding, filterValue) {
    const filter = String(filterValue || "").toLowerCase();
    const baseRows = this.normalizeRows(this.props.source || this.props.data || this.state.source);
    if (!filter) {
      this.setState({ source: baseRows });
      return;
    }
    this.setState({
      source: baseRows.filter((row) => String(row?.[binding] ?? "").toLowerCase().includes(filter)),
    });
  }

  setDataBinding = (datasource, sheetName, columns, callback) => {
    if (!datasource) return;
    this.setDataBinding2(datasource, sheetName, columns, callback);
  };

  setDataBinding2 = (datasource, sheetName, columns, callback) => {
    const bindingData = this.normalizeRows(datasource);
    const nextColumns = columns || this.state.columns;

    if (this.props.validateYmlFile) {
      getObjectFromYml(this.props.validateYmlFile).then((res) => {
        this.setState({ validateInfo: res });
      });
    }

    nextColumns.filter((column) => column.dataType).forEach((format) => {
      this.makeDataType(bindingData, format);
    });

    this.setState(
      {
        source: bindingData,
        columns: nextColumns,
      },
      () => {
        this.syncGridRef();
        if (callback) callback(this);
      }
    );
  };

  setEditDivVisible(value) {
    if (!this.useRefEditDiv.current) return;
    const display = this.useRefEditDiv.current.style.display;
    const nextDisplay = value == null || value === "" ? (display === "block" ? "none" : "block") : value ? "block" : "none";
    this.useRefEditDiv.current.style.display = nextDisplay;
  }

  setDefaultMergeManager() {}
  setCustomMergeManager() {}
  refresh() {
    this.forceUpdates();
  }
  findSelectedSheet() {}
  setMergeRange() {}

  saveSheetStyle() {
    const rtnObj = {
      url: "/E3/SYSTEM/SaveSheetStyle",
      option1: { method: "POST", body: [] },
      option2: { showConfirm: false },
    };
    this.props.onSaveStyle?.(rtnObj);
    return rtnObj;
  }

  fontChanged = () => {};
  fontSizeChanged = () => {};
  getSelectedCellStyle = () => ({});
  applyBoldStyle = () => {};
  applyUnderlineStyle = () => {};
  applyItalicStyle = () => {};
  colorPickerInit = () => {};
  showColorPicker = () => {};
  applyCellTextAlign = () => {};
  applyCellTextVerticalAlign = () => {};
  applyCustomCellMerge = () => {};
  applyCellEditStyle = (type, value, range) => {
    this.setState((prev) => ({
      cellEditStyle: [...prev.cellEditStyle, { sheetIdx: 0, type, value, range }],
    }));
  };

  colToNum = (col) => {
    if (!isNaN(col)) return Number(col);
    let num = 0;
    for (let i = 0; i < col.length; i++) {
      num = num * 26 + (col.charCodeAt(i) - "A".charCodeAt(0) + 1);
    }
    return num - 1;
  };

  numToCol = (num) => {
    let value = Number(num) + 1;
    let col = "";
    while (value > 0) {
      const remainder = value % 26;
      if (remainder === 0) {
        col = `Z${col}`;
        value = Math.floor(value / 26) - 1;
      } else {
        col = `${String.fromCharCode("A".charCodeAt(0) + remainder - 1)}${col}`;
        value = Math.floor(value / 26);
      }
    }
    return col;
  };

  makeDataType(data, format) {
    if (format.dataType === "Date" || format.dataType === "Time") {
      data.forEach((row) => {
        row[format.binding] = row[format.binding] ? new Date(row[format.binding]) : null;
      });
    } else if (format.dataType === "Number") {
      data.forEach((row) => {
        row[format.binding] = row[format.binding] ? Number(row[format.binding]) : null;
      });
    } else if (format.dataType === "Boolean") {
      data.forEach((row) => {
        row[format.binding] = Boolean(row[format.binding]);
      });
    }
  }

  setIsReadOnly = (value) => {
    this.setState({ isReadOnly: value });
  };

  getIsReadOnly = () => this.state.isReadOnly;

  setColumns = (columns) => {
    this.setState({ columns });
  };

  setColumngroups = (colgroup) => {
    this.setState({ columnGroups: colgroup });
  };

  getRows = () => this.state.source || [];

  getRowsByIndex = (idx) => this.getRows()[idx];

  getCheckedRows = () => this.getRows().filter((row) => row?.[this.props.checkBoxColumn]);

  getSelectedRow = () => this.getRows()[this.state.selectedRowIndex];

  getSelectedRowIndex = () => this.state.selectedRowIndex;

  getRowsCount = () => this.getRows().length;

  getAddedRows = () => this.getRows().filter((row) => row.ROW_STATUS === 1);

  getEditedRows = () => this.getRows().filter((row) => row.ROW_STATUS === 2);

  getDeletedRows = () => this.getRows().filter((row) => row.ROW_STATUS === 4);

  setToggleClass(cell, css, condition) {
    if (!cell || !css) return;
    const classes = Array.isArray(css) ? css : [css];
    classes.forEach((className) => cell.classList.toggle(className, Boolean(condition)));
  }

  clear = () => {
    this.setState({ source: [], columns: [], columnGroups: [] });
  };

  clearDataBinding = () => {
    this.setState({ source: [] });
  };

  addRow = () => {
    const newRow = { ROW_STATUS: 1, ROW_CHECK: true };
    const nextRows = [...this.getRows(), newRow];
    const nextIndex = nextRows.length - 1;
    this.setState({ source: nextRows, selectedRowIndex: nextIndex }, () => this.props.onRowAdded?.(this, { row: nextIndex }));
    return { row: nextIndex, dataItem: newRow };
  };

  removeRowAt = (idx) => {
    if (idx < 0) return;
    const rows = [...this.getRows()];
    rows.splice(idx, 1);
    this.setState({ source: rows, selectedRowIndex: Math.min(this.state.selectedRowIndex, rows.length - 1) });
  };

  deleteRow = (idx) => {
    const rowIndex = idx === 0 || idx ? idx : this.getRowsCount() - 1;
    const rows = [...this.getRows()];
    const target = rows[rowIndex];
    if (!target) return null;

    if (target.ROW_STATUS === 1) {
      rows.splice(rowIndex, 1);
    } else {
      rows[rowIndex] = { ...target, ROW_STATUS: 4, ROW_CHECK: true };
    }

    this.setState({ source: rows }, () => this.props.onDeletingRow?.(this, { row: rowIndex }));
    return { row: rowIndex, dataItem: target };
  };

  clearSelection = () => {
    this.setState({ selectedRowIndex: -1, selectedColumnIndex: -1 });
  };

  selectionByIndex = (idx, col = 0) => {
    this.setState({ selectedRowIndex: idx, selectedColumnIndex: col }, () => this.props.onRowClick?.());
  };

  clearAddRows = () => {
    this.setState({ source: this.getRows().filter((row) => row.ROW_STATUS !== 1) });
  };

  chkArray(arr) {
    return Boolean(arr && arr.length > 0);
  }

  async chkValidate(ymlFile, dataRowIndex, headerRowIndex) {
    const validateInfo = await getObjectFromYml(ymlFile);
    const rows = this.getRows();
    const columns = this.getExportColumns();
    const returnArray = [];

    for (let i = dataRowIndex; i < rows.length; i++) {
      columns.forEach((column) => {
        const colId = column.binding;
        const colName = !isNaN(headerRowIndex) && rows[headerRowIndex] ? rows[headerRowIndex][colId] : column.header;
        const validInfo = validateInfo[colId];
        if (!validInfo) return;

        try {
          validate(getValidateObject(validInfo, colName, rows[i][colId]));
        } catch (ex) {
          const exsplit = String(ex).split(":");
          returnArray.push({
            row: i + 1,
            col: colId,
            colNm: colName,
            msg: exsplit[exsplit.length - 1],
          });
        }
      });
    }

    return returnArray;
  }

  chkValidate2(ymlFile, dataRowIndex, headerRowIndex) {
    return this.chkValidate(ymlFile, dataRowIndex, headerRowIndex);
  }

  setFlatColumnGroups(columnGroups) {
    if (!columnGroups || columnGroups.length === 0) return [];
    const [prev, ...next] = columnGroups;
    if (Array.isArray(prev.columns)) return this.setFlatColumnGroups(prev.columns.concat(next));
    return [prev].concat(this.setFlatColumnGroups(next));
  }

  settingColumnGroups(columnGroups) {
    return columnGroups || [];
  }

  settingColumnGroupsCommon(column) {
    return column;
  }

  forceUpdates = () => {
    this.forceUpdate();
  };

  viewUpdates = (callback) => {
    this.setState({ source: [...this.getRows()] }, () => (callback ? callback(this) : null));
  };

  freezeRows = () => {};
  freezeColumns = () => {};
  removeCellMaker = () => {};
  addHeaderStyle = () => {};
  headerMultiLine = () => {};

  setCellData = (rowIndex, binding, value) => {
    const rows = [...this.getRows()];
    const row = { ...rows[rowIndex], [binding]: value };
    if (!row.ROW_STATUS) row.ROW_STATUS = 2;
    row.ROW_CHECK = true;
    rows[rowIndex] = row;
    this.setState({ source: rows }, () => this.props.onCellEditEnded?.(this, { row: rowIndex, binding, value }));
  };

  saveExcel = (colHeader = true, style = true, fileName, isCheckedItem = false) => {
    return saveGridLikeWorkbook([this.refGrid.current], {
      includeColumnHeaders: colHeader,
      includeStyles: style,
      fileName: fileName > "" ? fileName : "Sheet.xlsx",
      isCheckedItem,
    });
  };

  loadExcel = (headeRangeIdx = 0) => {
    this.headerRangeIdx = headeRangeIdx;
    this.useRefInput.current?.click();
  };

  loadExcelMakeGridData = async (file) => {
    if (!file) return;
    const rows = await readExcelRows(file, {
      headerRowIndex: this.headerRangeIdx,
      columns: this.getExportColumns(),
    });
    this.setDataBinding(rows);
    if (this.useRefInput.current) this.useRefInput.current.value = "";
  };

  getImportedDataFromWorkSheet() {
    return [];
  }
}

WorkbookGrid.propTypes = {
  style: PropTypes.object,
  theme: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.array,
  source: PropTypes.array,
  columns: PropTypes.array.isRequired,
  columnGroups: PropTypes.array,
  allowAddNew: PropTypes.bool,
  allowDelete: PropTypes.bool,
  selectionMode: PropTypes.string,
  onRowClick: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  sortDescriptions: PropTypes.array,
  groupDescriptions: PropTypes.array,
  trackChanges: PropTypes.bool,
  filter: PropTypes.bool,
  quickAutoSize: PropTypes.bool,
  allowPinning: PropTypes.string,
  isNo: PropTypes.bool,
  codeId: PropTypes.string,
  frozenRows: PropTypes.number,
  frozenColumns: PropTypes.number,
  allowMerging: PropTypes.string,
  isReadOnly: PropTypes.bool,
  exportExcel: PropTypes.bool,
  defaultColumn: PropTypes.bool,
  onCellUpdate: PropTypes.func,
  onCellEditEnding: PropTypes.func,
  onCellEditEnded: PropTypes.func,
  onRowAdded: PropTypes.func,
  onDeletingRow: PropTypes.func,
  beginningEdit: PropTypes.func,
  sortedColumn: PropTypes.func,
  isAllCheckbox: PropTypes.bool,
  checkBoxColumn: PropTypes.string,
  validateYmlFile: PropTypes.string,
  gridId: PropTypes.string,
  childItemsPath: PropTypes.string,
  isCollapseGroupsToLevel0: PropTypes.bool,
  onloadedRows: PropTypes.func,
  onFormatItem: PropTypes.func,
  isColumnFooters: PropTypes.bool,
  isInputFilter: PropTypes.bool,
  isSearchFilter: PropTypes.object,
  isDragColumn: PropTypes.bool,
  isAutoRowHeights: PropTypes.bool,
  headerStyle: PropTypes.array,
  columnFooterName: PropTypes.object,
  onSelectedSheetChanged: PropTypes.func,
  onSaveStyle: PropTypes.func,
};

WorkbookGrid.defaultProps = {
  width: "100%",
  height: "100%",
  data: [],
  source: [],
  columns: [],
  columnGroups: [],
  allowAddNew: true,
  allowDelete: true,
  selectionMode: "Cell",
  onRowClick: null,
  onRowDoubleClick: null,
  sortDescriptions: [],
  groupDescriptions: [],
  trackChanges: true,
  filter: false,
  quickAutoSize: true,
  allowPinning: "",
  isNo: true,
  frozenRows: 0,
  frozenColumns: 0,
  allowMerging: "Cells",
  isReadOnly: true,
  exportExcel: false,
  defaultColumn: true,
  onCellUpdate: null,
  onCellEditEnding: null,
  onCellEditEnded: null,
  onRowAdded: null,
  onDeletingRow: null,
  beginningEdit: null,
  sortedColumn: null,
  isAllCheckbox: false,
  checkBoxColumn: "CHK",
  validateYmlFile: null,
  gridId: null,
  childItemsPath: null,
  isCollapseGroupsToLevel0: false,
  onloadedRows: null,
  onFormatItem: null,
  isColumnFooters: false,
  isInputFilter: false,
  isSearchFilter: null,
  isDragColumn: false,
  isAutoRowHeights: false,
  headerStyle: [],
  columnFooterName: {},
  onSelectedSheetChanged: null,
  onSaveStyle: null,
};

export default WorkbookGrid;
